/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** Bookings — GET /re/bookings (status, project_id, broker_firm_id, from, to, search). */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@mui/material';
import MDButton from 'components/MDButton';
import { usePermission } from 'auth/usePermission';
import useDebounce from 'hooks/useDebounce';
import RePage, { FilterBar, LoadError, RePanel, ReSearch } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { RePicker, VocabSelect } from './components/Pickers';
import { DateField, Num } from './components/Widgets';
import {
  bookingStatuses, formatDate, formatMoney, formatPct, toNumber, useReList,
} from './api';
import { defaultCurrency } from '../variant';

export default function BookingsList() {
  const navigate = useNavigate();
  const { canCreate } = usePermission('re_bookings');
  const [status, setStatus] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [brokerId, setBrokerId] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 350);

  const list = useReList('/re/bookings', {
    status, project_id: projectId, broker_firm_id: brokerId, from, to, search: debounced,
    sort_by: 'booking_date', sort_order: 'desc',
  });

  const totals = useMemo(() => list.items.reduce((acc, b) => ({
    net: acc.net + (toNumber(b.net_price) || 0),
    paid: acc.paid + (toNumber(b.amount_paid) || 0),
  }), { net: 0, paid: 0 }), [list.items]);

  const columns = [
    { key: 'booking_number', label: 'Booking', render: (b) => <strong>{b.booking_number || `#${b.id}`}</strong>, footer: `Page total (${list.items.length})` },
    { key: 'booking_date', label: 'Date', render: (b) => formatDate(b.booking_date) },
    { key: 'project_name', label: 'Project' },
    { key: 'unit_number', label: 'Unit' },
    { key: 'buyer', label: 'Buyer', render: (b) => b.primary_contact_name || b.contact_name || '—' },
    { key: 'broker_firm_name', label: 'Broker', render: (b) => b.broker_firm_name || 'Direct' },
    {
      key: 'net_price', label: 'Net price', align: 'right',
      render: (b) => <Num>{formatMoney(b.net_price, b.currency, { decimals: 0 })}</Num>,
      footer: <Num bold>{formatMoney(totals.net, defaultCurrency(), { decimals: 0 })}</Num>,
    },
    {
      key: 'amount_paid', label: 'Paid', align: 'right',
      render: (b) => <Num>{formatMoney(b.amount_paid, b.currency, { decimals: 0 })}</Num>,
      footer: <Num bold>{formatMoney(totals.paid, defaultCurrency(), { decimals: 0 })}</Num>,
    },
    { key: 'paid_pct', label: 'Paid %', align: 'right', render: (b) => formatPct(b.paid_pct) },
    { key: 'status', label: 'Status', render: (b) => <ReStatusChip vocab="booking" status={b.status} size="xs" /> },
  ];

  return (
    <RePage
      title="Bookings"
      subtitle={`${list.total} booking${list.total === 1 ? '' : 's'}`}
      actions={canCreate && (
        <MDButton variant="gradient" color="info" size="small" onClick={() => navigate('/real-estate/bookings/new')}>
          <Icon>add</Icon>&nbsp;New booking
        </MDButton>
      )}
    >
      <FilterBar>
        <ReSearch label="Search booking no. / buyer" value={search} onChange={setSearch} sx={{ minWidth: 240 }} />
        <VocabSelect label="Status" value={status} onChange={setStatus} values={bookingStatuses()} />
        <RePicker path="/re/projects" label="Project" value={projectId} onChange={setProjectId} />
        <RePicker path="/re/broker-firms" label="Broker firm" value={brokerId} onChange={setBrokerId} />
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
      </FilterBar>
      <LoadError error={list.error} onRetry={list.reload} />
      <RePanel p={0}>
        <ReTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          emptyMessage="No bookings match these filters."
          onRowClick={(b) => navigate(`/real-estate/bookings/${b.id}`)}
          total={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
        />
      </RePanel>
    </RePage>
  );
}
